import React from "react";

import { isEqual, difference, min, pull, find, isEmpty, every, each } from "../../utils/lodash";
import { QuestionModel } from "../../models/QuestionModel";
import { BaseControl, ControlProps } from "./BaseControl";
import { RowType } from "../../models/enums/RowType";
import { ChoiceModel } from "../../models/ChoiceModel";
import { ValidationResultModel } from "../../models/ValidationResultModel";
import {
    getSettingString,
    hasSetting,
    getSettingNumber,
    getSettingBoolean,
} from "../../models/utilities/settingUtilities";
import { isNullOrEmpty, getContainerDiv } from "../../utils";
import { Signal } from "../../signals";
import { ValidationBuilder } from "../../models/builders/ValidationBuilder";
import { getRendererProps } from "../renderers/BaseRenderer";
import {
    variableForCell,
    openEndVariableName,
    isSingleChoice,
    isHeaderRow,
    hasOpenEnd,
} from "../../models/utilities/gridUtilities";
import { AxisModel } from "../../models/AxisModel";
import { ValidationOptionsModel } from "../../models/ValidationOptionsModel";
import { translate } from "../../models/utilities/translationUtilities";
import { __getStore__ } from "../../redux/store";
import GridRenderer from "../renderers/v2/GridRenderer";
import { isVisible } from "../../models/utilities/choiceUtilities";
import { ElementWithSettings } from "../../models/ElementWithSettings";
import { VariableModel } from "../../models/VariableModel";
import { QuestionType } from "../../models/enums/QuestionType";
import { SurveyMode } from "../../models/enums/SurveyMode";
import { ThunkDispatchProp } from "../../models/actions/ThunkDispatchProp";
import {
    getSoftPromptCode,
    shouldSetSoftPromptCode,
} from "../../models/utilities/questionUtilities";
import { isQuestionValidatedServerSide } from "../../models/utilities/validationUtilities";
import { evaluateIsVariableVisible } from "../../redux/thunks/evaluator";
import { enableAllMappedVariablesFieldStatus } from "../../redux/actions";

interface Props extends ControlProps, ThunkDispatchProp {}

interface State {
    validationResult?: ValidationResultModel;
    openEndValue: { [key: string]: string };
    expandedRowsIndices: number[];
}
export class GridControl extends BaseControl<Props, State> {
    validateSignal = new Signal<ValidationOptionsModel, ValidationResultModel>();
    tableRef: HTMLTableElement;
    accordionRefs: HTMLDivElement[] = [];

    constructor(props: Props) {
        super(props);
        this.state = {
            openEndValue: {},
            expandedRowsIndices: [],
        };
    }

    initializeQuestion(question: QuestionModel, props: Props): void {
        super.initializeQuestion(question, props);
        this.setState(
            {
                openEndValue: {},
                validationResult: null,
                expandedRowsIndices: [],
            },
            () => this.updateData(props),
        );
        this.initializeOpenEnd(question, props);
        this.setDefaultValues(props.sequencedVariables);
        // Remove mentions from rows no longer available
        each(question.rows, (r) => {
            if (
                this.axisHasMentions(r) &&
                every(props.sequencedRows, (sequencedRow) => sequencedRow.index !== r.index)
            ) {
                each(r.variables, (v) => {
                    this.setMentions(this.props, v.name, (m) => m.clear());
                });
            }
        });
    }

    setOpenEndState = (openEndName: string, openEndValue: string) => {
        this.setState((prevState) => {
            const newState = { ...prevState };
            newState.openEndValue[openEndName] = openEndValue;
            return newState;
        });
    };

    initializeOpenEnd(question: QuestionModel, props: Props) {
        question.rows.forEach((row) => {
            row.variables.forEach((variable) => {
                const answersWithOpenEnd = variable.choiceList.filter((answer) =>
                    hasSetting(answer, "OpenEndType"),
                );
                answersWithOpenEnd.forEach((answer) => {
                    const openEndName = openEndVariableName(answer);
                    const mentions = this.getMentions(props, openEndName);
                    const mention = mentions != null ? mentions[0] : undefined;
                    const openEndValue = !isNullOrEmpty(mention?.openEnd) ? mention.openEnd : "";
                    if (this.state.openEndValue[openEndName] !== openEndValue) {
                        this.setOpenEndState(openEndName, openEndValue);
                    }
                });
            });

            if (!hasSetting(row, "OpenEndType")) {
                return;
            }

            const openEndName = openEndVariableName(row);
            const mentions = this.getMentions(props, openEndName);
            const mention = mentions != null ? mentions[0] : undefined;
            const openEndValue = !isNullOrEmpty(mention?.openEnd) ? mention.openEnd : "";
            if (this.state.openEndValue[openEndName] !== openEndValue) {
                this.setOpenEndState(openEndName, openEndValue);
            }
        });
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        this.updateData(nextProps);
        this.initializeOpenEnd(nextProps.question, nextProps);
        super.UNSAFE_componentWillReceiveProps(nextProps);
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        super.componentDidUpdate(prevProps, prevState);
        const rows = this.props.sequencedRows;
        // When validation result changes and is invalid
        if (
            this.state.validationResult &&
            !this.state.validationResult.isValid &&
            !isEqual(prevState.validationResult, this.state.validationResult)
        ) {
            if (!this.hasMaxAnswers(this.props.question)) {
                // Expand all rows to make error messages visible
                this.setState((state) => {
                    const newState = { ...state };
                    newState.expandedRowsIndices = [
                        ...rows.filter((r) => !isHeaderRow(r)).map((r) => r.index),
                    ];
                    return newState;
                });
            } else {
                // Scroll to the first invalid row
                const firstInvalidRow = rows.find(
                    (row) => !isHeaderRow(row) && this.isRowInvalid(this.state, this.props, row),
                );
                if (firstInvalidRow && !this.isRowExpanded(firstInvalidRow.index)) {
                    this.onExpandRow(firstInvalidRow);
                }
            }
        }
    }

    didUpdateMentions(question: QuestionModel, prevProps: Props) {
        super.didUpdateMentions(question, prevProps);
        const { mentions } = this.props;
        const { mentions: prevMentions } = prevProps;
        const mentionKeys = Object.keys(mentions);
        const prevMentionKeys = Object.keys(prevMentions);
        if (this.hasMaxAnswers(question)) {
            // Get the new/changed mention ir order to decide which row should be collapsed
            const newMentions = difference(mentionKeys, prevMentionKeys);
            if (newMentions.length > 0) {
                newMentions.forEach((variableName) => {
                    this.openNextRow(variableName);
                });
            } else {
                mentionKeys.forEach((variableName) => {
                    if (!isEqual(mentions[variableName], prevMentions[variableName])) {
                        this.openNextRow(variableName);
                    }
                });
            }
        }
    }

    setDefaultValues(variables: VariableModel[]) {
        variables
            .filter((variable) => !this.hasMentions(this.props, variable.name))
            .forEach((variable) => {
                const { choiceList = [] } = variable;
                const defaultVisibleChoices = choiceList.filter(
                    (choice) => getSettingBoolean(choice, "Default") && isVisible(choice),
                );
                this.setChoicesAsMentions(variable, defaultVisibleChoices);
            });
    }

    updateData(props: Props) {
        const { sequencedRows } = props;
        if (isEmpty(this.state.expandedRowsIndices)) {
            this.setState((s, p) => {
                const newState = { ...s };
                if (
                    !this.hasMaxAnswers(p.question) ||
                    getSettingBoolean(p.question, "DisableAutoCollapse")
                ) {
                    newState.expandedRowsIndices.push(
                        ...sequencedRows.filter((row) => !isHeaderRow(row)).map((row) => row.index),
                    );
                } else {
                    const row = sequencedRows.find((r) => !isHeaderRow(r));
                    if (row) {
                        newState.expandedRowsIndices.push(row.index);
                    }
                }
                return newState;
            });
        }
    }

    isRowInvalid(state: State, props: Props, row: AxisModel) {
        return props.sequencedColumns.some((column) => {
            const cellVariable = variableForCell(row, column);
            const cellErrors = state.validationResult.errorsByVariableName[cellVariable.name];
            return cellErrors && cellErrors.length > 0;
        });
    }

    hasMaxAnswers = (question: QuestionModel) => this.getMaxAnswers(question) > 0;

    getMaxAnswers = (question: QuestionModel) => {
        if (isSingleChoice(question)) {
            return 1;
        }
        const rows = this.props.sequencedRows;
        const maxAnswers = getSettingNumber(question, "MaxAllowedAnswers", -1);
        const noneHeaderRows = rows.filter((row) => !isHeaderRow(row));
        const numberOfChoices = noneHeaderRows[0]?.variables[0]?.choiceList?.length ?? 0;

        return min([maxAnswers, numberOfChoices].filter(Boolean));
    };

    onIsRowSelected = (row: AxisModel) => {
        if (row.variables[0] == null) {
            return false;
        }
        return this.getMention(this.props, row.variables[0].name) != null;
    };

    onOpenEndChange = (answer: any, event: any) => {
        const { value } = event.target;
        this.setState((state) => {
            const newState = { ...state };
            newState.openEndValue = {
                ...newState.openEndValue,
                [openEndVariableName(answer)]: this.truncateOpenEnd(answer, value),
            };
            return newState;
        });
    };

    onOpenEndBlur = (answer: any) => {
        const openEndName = openEndVariableName(answer);
        const openEnd = this.state.openEndValue[openEndName];
        if (isNullOrEmpty(openEnd)) {
            this.setMentions(this.props, openEndName, (mentions) => mentions.clear());
        } else {
            this.setMentions(this.props, openEndName, (mentions) =>
                mentions.set(getSettingString(answer, "OpenEndAnswerCode", "0"), openEnd.trim()),
            );
        }
    };

    onExpandRow = (row: AxisModel) => {
        const { question } = this.props;
        this.setState((state) => {
            const newState = { ...state };
            newState.expandedRowsIndices = [...state.expandedRowsIndices];
            if (
                this.isRowExpanded(row.index, newState) &&
                !getSettingBoolean(question, "DisableAutoCollapse")
            ) {
                pull(newState.expandedRowsIndices, row.index);
            } else {
                newState.expandedRowsIndices = [row.index];
            }
            return newState;
        });
    };

    onAddAccordionRef = (index: number, ref: HTMLDivElement) => {
        this.accordionRefs[index] = ref;
    };

    openNextRow(currentRowVariableName: string) {
        if (
            this.getMentions(this.props, currentRowVariableName).length ===
            this.getMaxAnswers(this.props.question)
        ) {
            const rows = this.props.sequencedRows;
            const currentRow = rows.find((row) =>
                row.variables.map((variable) => variable.name).includes(currentRowVariableName),
            );
            if (currentRow && !hasOpenEnd(currentRow)) {
                const nextRow = rows.find(
                    (row, index) =>
                        !isHeaderRow(row) &&
                        index > this.getRowPosition(currentRow) &&
                        !this.axisHasMentions(row),
                );
                if (nextRow) {
                    if (!this.isRowExpanded(nextRow.index)) {
                        this.adjustScrollPosition(currentRow);
                        this.onExpandRow(nextRow);
                    }
                } else {
                    this.onExpandRow(currentRow);
                }
            }
        }
    }

    adjustScrollPosition = (row: AxisModel) => {
        const ref = this.accordionRefs[row.index];
        if (ref) {
            const rects = ref.getBoundingClientRect();
            if (rects.top < 0) {
                const { mode } = __getStore__().getState().application;
                const bannerHeight = mode !== SurveyMode.Normal ? -38 : 0;
                const container = getContainerDiv();
                container.scrollTo(
                    0,
                    container.scrollTop + rects.top + rects.height + bannerHeight,
                );
            }
        }
    };

    getRowPosition = (row: AxisModel) =>
        this.props.sequencedRows.findIndex((r) => r.index === row.index);

    isRowExpanded = (rowIndex: number, state: State = this.state) =>
        state.expandedRowsIndices.includes(rowIndex);

    cellHasMentions(row: AxisModel, column: AxisModel) {
        const { question } = this.props;
        const softPromptCode = shouldSetSoftPromptCode(question)
            ? getSoftPromptCode(question)
            : null;
        const variable = variableForCell(row, column);
        const mentions = this.getMentions(this.props, variable.name);
        return mentions.some((mention) => {
            const isMentionSoftPrompt = mention.value === softPromptCode;
            const mentionHasValue =
                mention.value !== undefined && mention.value !== null && !isMentionSoftPrompt;
            const mentionHasOpenEnd =
                mention.openEnd !== undefined &&
                mention.openEnd !== null &&
                mention.openEnd.length > 0;
            return mentionHasValue || mentionHasOpenEnd;
        });
    }

    onShouldHideCell = (row: AxisModel, column: AxisModel) => {
        const variable = variableForCell(row, column);
        if (!variable || variable.skipLogic == null) {
            return false;
        }
        const { dispatch } = this.props;
        const isHidden = !dispatch(evaluateIsVariableVisible(variable));

        if (isHidden) {
            if (this.hasMentions(this.props, variable.name)) {
                const variableAllMentions = this.getLatestMentionsForVariableName(variable.name);
                dispatch(enableAllMappedVariablesFieldStatus(variable.name, variableAllMentions));

                this.setMentions(this.props, variable.name, (m) => m.clear());
            }
        }

        return isHidden;
    };

    isChoiceSelected = (variable: VariableModel, choice: ChoiceModel) => {
        const mentions = this.getMentions(this.props, variable.name);
        return mentions.some((mention) => mention.value === choice.value);
    };

    isColumnChoiceSelected = (column: AxisModel, choice: ChoiceModel) =>
        this.props.sequencedRows.some((row) => {
            if (row.type !== RowType.Variable) {
                return false;
            }
            const cellVariable = variableForCell(row, column);
            return this.isChoiceSelected(cellVariable, choice);
        });

    onShouldDisableCell = (row: AxisModel, column: AxisModel) => {
        const { sequencedColumns } = this.props;
        const isCellDisabled = sequencedColumns
            .filter((sequencedColumn) => sequencedColumn.variables[0])
            .some((sequencedColumn) => {
                const cellVariable = variableForCell(row, sequencedColumn);
                return cellVariable.choiceList.some((choice) => {
                    const disabledColumns = getSettingString(choice, "DisableColumns");
                    if (!isNullOrEmpty(disabledColumns)) {
                        const mentions = this.getMentions(this.props, cellVariable.name);
                        if (mentions.some((m) => m.value === choice.value)) {
                            const isDisabled = disabledColumns
                                .replace(/\s/g, "")
                                .split(",")
                                .find((c) => c === column.index.toString());
                            if (isDisabled) {
                                const currentCell = variableForCell(row, column);
                                const currentMentions = this.getMentions(
                                    this.props,
                                    currentCell.name,
                                );
                                if (currentMentions && currentMentions.length > 0) {
                                    this.setMentions(this.props, currentCell.name, (m) =>
                                        m.clear(),
                                    );
                                }
                                return true;
                            }
                        }
                    }
                    return false;
                });
            });
        return isCellDisabled;
    };

    setChoicesAsMentions = (element: ElementWithSettings, choices: ChoiceModel[]) => {
        const values = choices.map((choice) => ({ value: choice.value }));
        if (values.length) {
            this.setMentions(
                this.props,
                element.name,
                (mentions) => mentions.clear().add(values),
                undefined,
                true,
            );
        }
    };

    validate(options?: ValidationOptionsModel): ValidationResultModel {
        const { question, useClientValidation } = this.props;
        question.variables.forEach(this.removeUnavailableMentions);
        this.removeHiddenOrDisabledOpenEndMentions(this.props);

        if (isQuestionValidatedServerSide(question.type, useClientValidation)) {
            return new ValidationBuilder().build();
        }

        const softPromptValidationResult = this.validateSoftPrompt(this.props);
        if (!softPromptValidationResult.isValid) {
            return softPromptValidationResult;
        }
        const isSingleStarRatingQuestion =
            question.type === QuestionType.StarRatingGrid && question.variables.length === 1;
        if (isSingleStarRatingQuestion) {
            const row = find(question.rows, (r) => !isEmpty(r.variables));
            const [column] = question.columns;
            return this.onValidateCell(row, column);
        }
        const results = this.validateSignal.dispatch(options);
        const result = results.reduce(
            (a, b) => ({
                isValid: a.isValid && b.isValid,
                errorsByVariableName: { ...a.errorsByVariableName, ...b.errorsByVariableName },
            }),
            { isValid: true, errorsByVariableName: {} },
        );
        if (!options || !options.silent) {
            this.setState((prevState) => ({ ...prevState, validationResult: result }));
        }
        return result;
    }

    removeHiddenOrDisabledOpenEndMentions(props: Props) {
        const { sequencedRows: rows, sequencedColumns: columns } = props;
        [...rows, ...columns].forEach((axisItem) => {
            if (axisItem.openEndActive !== false) {
                return;
            }
            const openEndVariable = openEndVariableName(axisItem);
            if (isNullOrEmpty(openEndVariable)) {
                return;
            }
            const openEndHasMentions = this.hasMentions(this.props, openEndVariable);
            if (!openEndHasMentions) {
                return;
            }
            this.setMentions(this.props, openEndVariable, (mentions) => mentions.clear());
        });
    }

    validateRowOpenEnd = (
        row: AxisModel,
        openEndValue: { [key: string]: string },
    ): ValidationResultModel => {
        if (hasSetting(row, "OpenEndType")) {
            const openEndName = openEndVariableName(row);
            const openEnd = openEndValue[openEndName];

            if (row.openEndActive) {
                if (!isNullOrEmpty(openEnd.trim()) || this.axisHasMentions(row)) {
                    const result = this.validateOpenEnd(openEnd, row, openEndName);
                    if (!result.isValid) {
                        return result;
                    }
                }
            } else if (!isNullOrEmpty(openEnd)) {
                this.setMentions(this.props, openEndName, (mentions) => mentions.clear());
            }
        }
        return new ValidationBuilder().build();
    };

    onValidateRowOpenEnd = (row: AxisModel): ValidationResultModel =>
        this.validateRowOpenEnd(row, this.state.openEndValue);

    onValidateChoiceOpenEnd = (column: AxisModel, choice: ChoiceModel): ValidationResultModel => {
        // Check if the choice has an enforced open-end.
        if (hasSetting(choice, "OpenEndType")) {
            // Check if this choice is selected in any row.
            const choiceSelected = this.isColumnChoiceSelected(column, choice);
            const openEndVariable = openEndVariableName(choice);
            const openEnd = this.state.openEndValue[openEndVariable];

            if (choiceSelected || !isNullOrEmpty(openEnd.trim())) {
                const result = this.validateOpenEnd(openEnd, choice, openEndVariable);
                if (!result.isValid) {
                    return result;
                }
            }
        }
        return new ValidationBuilder().build();
    };

    onValidateCell = (row: AxisModel, column: AxisModel): ValidationResultModel => {
        const { questionnaire, question, language } = this.props;
        const validation = new ValidationBuilder();

        const isAnswerRequired =
            this.isAnswerRequired(questionnaire, question) &&
            this.isAxisAnswerRequired(row) &&
            this.isAxisAnswerRequired(column);
        const isCellDisabled = this.onShouldDisableCell(row, column);
        const cellHasMentions = this.cellHasMentions(row, column);

        // Validate right away if nothing is required.
        if (isCellDisabled || (!isAnswerRequired && !cellHasMentions)) {
            return validation.build();
        }

        const variable = question.variables.find(
            (unfilteredVariable) => row.variables[0].name === unfilteredVariable.name,
        );

        const cellVariable = variableForCell(row, column);
        const customError = translate(question, "CustomErrorMessage", language);

        if (cellHasMentions) {
            // Check the minimum and maximum answers if an answer was selected
            const mentions = this.getMentions(this.props, cellVariable.name);
            // For HybridGrid, the min and max are defined per column
            const minValue = this.minValue() || column.settings.MinRequiredAnswers;
            const maxValue = this.maxValue() || column.settings.MaxAllowedAnswers;
            if (minValue !== null && mentions.length < minValue) {
                return validation
                    .fail(cellVariable.name, customError || "InvalidChoicesMin", {
                        min: String(minValue),
                    })
                    .build();
            }
            if (maxValue !== null && mentions.length > maxValue) {
                return validation
                    .fail(cellVariable.name, customError || "InvalidChoicesMax", {
                        max: String(maxValue),
                    })
                    .build();
            }
        } else {
            const { choiceList } = variable;
            const invisibleDefaultChoices = choiceList.filter(
                (choice) => getSettingBoolean(choice, "Default") && !isVisible(choice),
            );
            if (invisibleDefaultChoices.length) {
                this.setChoicesAsMentions(variable, invisibleDefaultChoices);
                return validation.build();
            }
            return this.validateSoftPrompt(this.props, [cellVariable]);
        }

        return validation.build();
    };

    renderQuestion(): JSX.Element {
        return (
            <GridRenderer
                {...getRendererProps(this.props)}
                columns={this.props.sequencedColumns}
                rows={this.props.sequencedRows}
                validateSignal={this.validateSignal}
                isRowSelected={this.onIsRowSelected}
                expandRow={this.onExpandRow}
                addAccordionRef={this.onAddAccordionRef}
                expandedRowsIndices={this.state.expandedRowsIndices}
                shouldHideCell={this.onShouldHideCell}
                shouldDisableCell={this.onShouldDisableCell}
                validationResult={this.state.validationResult}
                openEndValue={this.state.openEndValue}
                validateCell={this.onValidateCell}
                validateRowOpenEnd={this.onValidateRowOpenEnd}
                validateChoiceOpenEnd={this.onValidateChoiceOpenEnd}
                onOpenEndBlur={this.onOpenEndBlur}
                onOpenEndChange={this.onOpenEndChange}
                showSoftPrompt={this.props.showSoftPrompt}
                matrix={this.props.matrix}
                isColumnChoiceSelected={this.isColumnChoiceSelected}
                max={this.props.max}
                sequencedVariables={this.props.sequencedVariables}
            />
        );
    }
}
